/**
 * useDocumentSources Hook
 * Standardized API hooks for document source connection operations
 *
 * Refactored: Phase 1 - Factory Pattern
 * Pattern: Uses createQueryHook and createMutationHook factories for code reduction
 */

import type {
  DocumentSourceUserConnectionRequest,
  DocumentSourceProviderConfigResponse,
  DocumentSourceUserConnectionResponse,
  DocumentSourceSyncStatsResponse,
} from '../../api/data-contracts';
import { QUERY_STALE_TIME } from '../../constants';
import { createQueryHook, createMutationHook } from './factories';
import { log } from '../../utils/errors/logger';

// --- Query Hooks ---

/**
 * Get available document source providers
 */
export const useAvailableProviders = createQueryHook<DocumentSourceProviderConfigResponse[], void>({
  queryKey: () => ['document-sources', 'providers'] as const,
  queryFn: (apiClient) =>
    apiClient.getAvailableProvidersApiDocumentSourcesProvidersGet().then(response => response.data),
  logContext: 'GET /api/document-sources/providers',
  staleTime: QUERY_STALE_TIME.LONG,
  onSuccess: (data) => {
    log.api.response('GET', '/api/document-sources/providers', 200, {
      dataLength: Array.isArray(data) ? data.length : 'not-array'
    });
  },
});

/**
 * Get user document source connections
 */
export const useUserConnections = createQueryHook<DocumentSourceUserConnectionResponse[], void>({
  queryKey: () => ['document-sources', 'user-connections'] as const,
  queryFn: (apiClient) =>
    apiClient.getUserConnectionsApiDocumentSourcesUserConnectionsGet().then(response => response.data),
  logContext: 'GET /api/document-sources/user/connections',
  staleTime: QUERY_STALE_TIME.SHORT,
  onSuccess: (data) => {
    log.api.response('GET', '/api/document-sources/user/connections', 200, {
      dataLength: Array.isArray(data) ? data.length : 'not-array'
    });
  },
});

/**
 * Get a specific user connection
 */
export const useUserConnection = createQueryHook<DocumentSourceUserConnectionResponse, string>({
  queryKey: (connectionId) => ['document-sources', 'user-connections', connectionId] as const,
  queryFn: (apiClient, connectionId) =>
    apiClient.getUserConnectionApiDocumentSourcesUserConnectionsConnectionIdGet(connectionId).then(response => response.data),
  logContext: 'GET /api/document-sources/user/connections/:id',
  staleTime: QUERY_STALE_TIME.SHORT,
});

/**
 * Get sync status for a connection
 */
export const useConnectionSyncStatus = createQueryHook<Record<string, unknown>, string>({
  queryKey: (connectionId) => ['document-sources', 'user-connections', connectionId, 'sync-status'] as const,
  queryFn: (apiClient, connectionId) =>
    apiClient.getSyncStatusApiDocumentSourcesUserConnectionsConnectionIdSyncStatusGet(connectionId).then(response => response.data),
  logContext: 'GET /api/document-sources/user/connections/:id/sync-status',
  staleTime: 0, // Always fetch fresh sync status
});

/**
 * Get connection statistics
 */
export const useConnectionStats = createQueryHook<DocumentSourceSyncStatsResponse, string>({
  queryKey: (connectionId) => ['document-sources', 'user-connections', connectionId, 'stats'] as const,
  queryFn: (apiClient, connectionId) =>
    apiClient.getConnectionStatsApiDocumentSourcesUserConnectionsConnectionIdStatsGet(connectionId).then(response => response.data),
  logContext: 'GET /api/document-sources/user/connections/:id/stats',
  staleTime: QUERY_STALE_TIME.MEDIUM,
});

/**
 * Get recursive file scan results
 */
export const useRecursiveFileScan = createQueryHook<
  Record<string, unknown>,
  { connectionId: string; max_depth?: number }
>({
  queryKey: ({ connectionId, max_depth }) =>
    ['document-sources', 'user-connections', connectionId, 'recursive-scan', { max_depth }] as const,
  queryFn: (apiClient, { connectionId, max_depth }) =>
    apiClient.recursiveFileScanApiDocumentSourcesUserConnectionsConnectionIdRecursiveScanGet(
      connectionId,
      max_depth
    ).then(response => response.data),
  logContext: 'GET /api/document-sources/user/connections/:id/recursive-scan',
  staleTime: 2 * QUERY_STALE_TIME.MEDIUM, // 2 minutes
});

// --- Mutation Hooks ---

/**
 * Create a new user connection
 */
export const useCreateUserConnection = createMutationHook<
  DocumentSourceUserConnectionResponse,
  DocumentSourceUserConnectionRequest
>({
  mutationFn: (apiClient, connectionData) =>
    apiClient.createUserConnectionApiDocumentSourcesUserConnectionsPost(connectionData).then(response => response.data),
  invalidateQueries: [['document-sources', 'user-connections']],
  logContext: 'POST /api/document-sources/user/connections',
  logMethod: 'POST',
  onSuccess: () => {
    log.user.action('connection_created', 'useDocumentSources');
  },
});

/**
 * Update a user connection
 */
export const useUpdateUserConnection = createMutationHook<
  DocumentSourceUserConnectionResponse,
  { connectionId: string; data: DocumentSourceUserConnectionRequest }
>({
  mutationFn: (apiClient, { connectionId, data }) =>
    apiClient.updateUserConnectionApiDocumentSourcesUserConnectionsConnectionIdPut(connectionId, data).then(response => response.data),
  invalidateQueries: [['document-sources', 'user-connections']],
  logContext: 'PUT /api/document-sources/user/connections/:id',
  logMethod: 'PUT',
  onSuccess: (_data, variables) => {
    log.user.action('connection_updated', 'useDocumentSources', { connectionId: variables.connectionId });
  },
});

/**
 * Delete a user connection
 */
export const useDeleteUserConnection = createMutationHook<Record<string, unknown>, string>({
  mutationFn: (apiClient, connectionId) =>
    apiClient.deleteUserConnectionApiDocumentSourcesUserConnectionsConnectionIdDelete(connectionId).then(response => response.data),
  invalidateQueries: [['document-sources', 'user-connections']],
  logContext: 'DELETE /api/document-sources/user/connections/:id',
  logMethod: 'DELETE',
  onSuccess: (_data, connectionId) => {
    log.user.action('connection_deleted', 'useDocumentSources', { connectionId });
  },
});

/**
 * Trigger sync for a connection
 */
export const useSyncConnection = createMutationHook<Record<string, unknown>, string>({
  mutationFn: (apiClient, connectionId) =>
    apiClient.syncUserConnectionApiDocumentSourcesUserConnectionsConnectionIdSyncPost(connectionId).then(response => response.data),
  invalidateQueries: [['document-sources', 'user-connections']],
  logContext: 'POST /api/document-sources/user/connections/:id/sync',
  logMethod: 'POST',
  onSuccess: (_data, connectionId) => {
    log.user.action('connection_synced', 'useDocumentSources', { connectionId });
  },
});

/**
 * Parse SharePoint link
 */
export const useParseSharePointLink = createMutationHook<
  Record<string, unknown>,
  { sharepoint_url: string }
>({
  mutationFn: (apiClient, params) =>
    apiClient.parseSharepointLinkApiDocumentSourcesUserConnectionsParseSharepointLinkPost(params).then(response => response.data),
  logContext: 'POST /api/document-sources/user/connections/parse-sharepoint-link',
  logMethod: 'POST',
});

/**
 * Browse SharePoint files
 */
export const useBrowseSharePointFiles = createMutationHook<
  Record<string, unknown>,
  { connectionId: string; params: { folder_path?: string; max_depth?: number } }
>({
  mutationFn: (apiClient, { connectionId, params }) =>
    apiClient.browseSharepointFilesApiDocumentSourcesUserConnectionsConnectionIdBrowseFilesPost(
      connectionId,
      params
    ).then(response => response.data),
  logContext: 'POST /api/document-sources/user/connections/:id/browse-files',
  logMethod: 'POST',
});

/**
 * Sync selected files
 */
export const useSyncSelectedFiles = createMutationHook<
  Record<string, unknown>,
  { connectionId: string; params: { selected_tree: Record<string, unknown>; sync_mode: string } }
>({
  mutationFn: (apiClient, { connectionId, params }) =>
    apiClient.syncSelectedFilesApiDocumentSourcesUserConnectionsConnectionIdSyncSelectedPost(
      connectionId,
      params
    ).then(response => response.data),
  invalidateQueries: [['document-sources', 'user-connections']],
  logContext: 'POST /api/document-sources/user/connections/:id/sync-selected',
  logMethod: 'POST',
  onSuccess: (_data, variables) => {
    log.user.action('selected_files_synced', 'useDocumentSources', { connectionId: variables.connectionId });
  },
});

// ============================================================================
// Admin Provider Configuration Hooks
// ============================================================================

export const documentSourceQueryKeys = {
  all: ['document-sources'] as const,
  providers: () => [...documentSourceQueryKeys.all, 'providers'] as const,
  adminProviderConfigs: () => [...documentSourceQueryKeys.all, 'admin-provider-configs'] as const,
  userConnections: () => [...documentSourceQueryKeys.all, 'user-connections'] as const,
};

/**
 * Hook to get all admin-configured document source providers (Admin only).
 * This returns the actual configured provider instances, not just available provider types.
 */
export const useAdminProviderConfigs = createQueryHook<DocumentSourceProviderConfigResponse[], void>({
  queryKey: () => documentSourceQueryKeys.adminProviderConfigs(),
  queryFn: (apiClient) =>
    apiClient.getProviderConfigsApiDocumentSourcesAdminProviderConfigsGet().then(response => response.data),
  logContext: 'GET /api/document-sources/admin/provider-configs',
  staleTime: QUERY_STALE_TIME.MEDIUM,
  onSuccess: (data) => {
    log.api.response('GET', '/api/document-sources/admin/provider-configs', 200, {
      dataLength: Array.isArray(data) ? data.length : 'not-array'
    });
  },
});

/**
 * Hook to create a new document source provider configuration (Admin only).
 */
export const useCreateProviderConfig = createMutationHook<
  DocumentSourceProviderConfigResponse,
  Record<string, unknown>
>({
  mutationFn: (apiClient, config) =>
    apiClient.createProviderConfigApiDocumentSourcesAdminProviderConfigsPost(config).then(response => response.data),
  invalidateQueries: [
    documentSourceQueryKeys.adminProviderConfigs(),
  ],
  logContext: 'POST /api/document-sources/admin/provider-configs',
  logMethod: 'POST',
  onSuccess: () => {
    log.user.action('provider_config_created', 'useDocumentSources');
  },
});

/**
 * Hook to update an existing provider configuration (Admin only).
 */
export const useUpdateProviderConfig = createMutationHook<
  DocumentSourceProviderConfigResponse,
  { configId: string; data: Record<string, unknown> }
>({
  mutationFn: (apiClient, { configId, data }) =>
    apiClient.updateProviderConfigApiDocumentSourcesAdminProviderConfigsConfigIdPut(configId, data).then(response => response.data),
  invalidateQueries: [
    documentSourceQueryKeys.adminProviderConfigs(),
  ],
  logContext: 'PUT /api/document-sources/admin/provider-configs/:configId',
  logMethod: 'PUT',
  onSuccess: (_data, variables) => {
    log.user.action('provider_config_updated', 'useDocumentSources', { configId: variables.configId });
  },
});

/**
 * Hook to delete a provider configuration (Admin only).
 */
export const useDeleteProviderConfig = createMutationHook<Record<string, unknown>, string>({
  mutationFn: (apiClient, configId) =>
    apiClient.deleteProviderConfigApiDocumentSourcesAdminProviderConfigsConfigIdDelete(configId).then(response => response.data),
  invalidateQueries: [
    documentSourceQueryKeys.adminProviderConfigs(),
  ],
  logContext: 'DELETE /api/document-sources/admin/provider-configs/:configId',
  logMethod: 'DELETE',
  onSuccess: (_data, configId) => {
    log.user.action('provider_config_deleted', 'useDocumentSources', { configId });
  },
});
